/**
 * LA SAISIE D'UNE FIGURE DE FONCTION — partagée par l'atelier et la révision.
 *
 * Le fondateur, 15 septembre 2026 : « on doit pouvoir supprimer un exercice ou
 * modifier une figure ou un exercice avec figure, c'est ça de l'édition ».
 *
 * Écrire deux fois ce formulaire — une fois pour créer, une fois pour corriger
 * — garantirait qu'ils divergent : le jour où une famille de fonctions
 * s'ajoute, l'un des deux l'aurait et pas l'autre. Il vit donc ici, et les deux
 * écrans l'appellent.
 *
 * AUCUNE EXPRESSION LIBRE n'est saisie, jamais : quatre familles fermées et des
 * coefficients numériques. C'est ce qui rend le tracé déterministe, et ce qui
 * interdit qu'une formule tapée devienne du code exécuté.
 */
import { resoudreFigure, type FigureSpecDto, type GrapheSpecDto } from '@silex/shared';

import { FigureSvg } from './FigureSvg';

export type FamilleFonction = 'lineaire' | 'quadratique' | 'sinusoidale' | 'exponentielle';

export interface Coefficients {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
}

/** Les coefficients que chaque famille utilise VRAIMENT — le reste n'est pas montré. */
const COEFS_PAR_FAMILLE: Readonly<Record<FamilleFonction, readonly (keyof Coefficients)[]>> = {
  lineaire: ['a', 'b'],
  quadratique: ['a', 'b', 'c'],
  sinusoidale: ['a', 'b', 'c', 'd'],
  exponentielle: ['a', 'b', 'c'],
};

const LIBELLE_FAMILLE: Readonly<Record<FamilleFonction, string>> = {
  lineaire: 'Linéaire — a x + b',
  quadratique: 'Quadratique — a x² + b x + c',
  sinusoidale: 'Sinusoïdale — a sin(b x + c) + d',
  exponentielle: 'Exponentielle — a b^x + c',
};

/**
 * LA SPÉCIFICATION DE FIGURE, assemblée depuis les champs.
 *
 * AUCUNE EXPRESSION LIBRE n'est saisie, jamais : quatre familles fermées et des
 * coefficients numériques. C'est ce qui rend le tracé déterministe et
 * vérifiable, et ce qui interdit qu'une formule tapée devienne du code exécuté.
 *
 * Elle part telle quelle au `FigureSvg`, qui la résout lui-même : ce que le
 * professeur voit dans l'aperçu est donc exactement ce que le moteur sait
 * tracer. S'il n'y arrive pas, il n'y a pas d'aperçu — et c'est l'information
 * utile, plutôt qu'un dessin approximatif présenté comme fiable.
 */
/**
 * La fonction, construite famille par famille.
 *
 * Un assemblage générique (`Object.fromEntries`) effacerait les types : le
 * compilateur ne verrait plus les coefficients, et il faudrait une conversion
 * forcée — c'est-à-dire masquer exactement le genre d'écart qui produirait plus
 * tard une figure jamais servie. Chaque branche est donc écrite en clair.
 */
function fonctionDe(
  famille: FamilleFonction,
  coef: Coefficients,
  domaine: { xMin: number; xMax: number },
): GrapheSpecDto {
  const base = { titre: 'f', domaine } as const;
  switch (famille) {
    case 'lineaire':
      return { famille, ...base, a: coef.a, b: coef.b };
    case 'quadratique':
      return { famille, ...base, a: coef.a, b: coef.b, c: coef.c };
    case 'sinusoidale':
      return { famille, ...base, a: coef.a, b: coef.b, c: coef.c, d: coef.d };
    case 'exponentielle':
      return { famille, ...base, a: coef.a, b: coef.b, c: coef.c };
  }
}

/**
 * LA SPÉCIFICATION DE FIGURE, assemblée depuis les champs.
 *
 * AUCUNE EXPRESSION LIBRE n'est saisie, jamais : quatre familles fermées et des
 * coefficients numériques. C'est ce qui rend le tracé déterministe, et ce qui
 * interdit qu'une formule tapée devienne du code exécuté.
 *
 * Elle part telle quelle au `FigureSvg`, qui la résout lui-même : ce que le
 * professeur voit dans l'aperçu est exactement ce que le moteur sait tracer.
 * S'il n'y arrive pas, il n'y a pas d'aperçu — et c'est l'information utile,
 * plutôt qu'un dessin approximatif présenté comme fiable.
 */
export function specDeLaCourbe(
  famille: FamilleFonction,
  coef: Coefficients,
  domaine: { xMin: number; xMax: number; yMin: number; yMax: number },
  pas: { x: number; y: number },
): FigureSpecDto {
  return {
    famille: 'repere',
    titre: 'Représentation graphique',
    domaine,
    pas,
    objets: [
      {
        type: 'courbe',
        fonction: fonctionDe(famille, coef, { xMin: domaine.xMin, xMax: domaine.xMax }),
      },
    ],
  };
}

export const COEF_PAR_DEFAUT: Coefficients = { a: 1, b: 0, c: 0, d: 0 };
export const DOMAINE_PAR_DEFAUT = { xMin: -5, xMax: 5, yMin: -5, yMax: 5 };
export const PAS_PAR_DEFAUT = { x: 1, y: 1 };

/** Les six familles du contrat de figure — ce que le moteur sait résoudre. */
export const FAMILLES_FIGURE = [
  'fonction',
  'triangle',
  'cercle',
  'polygone',
  'solide',
  'diagramme',
] as const;
export type FamilleFigure = (typeof FAMILLES_FIGURE)[number];

export const LIBELLE_FAMILLE_FIGURE: Readonly<Record<FamilleFigure, string>> = {
  fonction: 'Représentation d’une fonction',
  triangle: 'Triangle',
  cercle: 'Cercle',
  polygone: 'Polygone',
  solide: 'Solide',
  diagramme: 'Diagramme',
};

export const UNITES = ['mm', 'cm', 'm', 'km', 'u'] as const;
export type Unite = (typeof UNITES)[number];

export const CONSTRUCTIONS_TRIANGLE = [
  'trois_cotes',
  'deux_cotes_angle',
  'cote_deux_angles',
  'rectangle',
] as const;
export type ConstructionTriangle = (typeof CONSTRUCTIONS_TRIANGLE)[number];

export const LIBELLE_CONSTRUCTION: Readonly<Record<ConstructionTriangle, string>> = {
  trois_cotes: 'Trois côtés',
  deux_cotes_angle: 'Deux côtés et l’angle entre eux',
  cote_deux_angles: 'Un côté et les deux angles adjacents',
  rectangle: 'Rectangle en un sommet',
};

export const FORMES_POLYGONE = [
  'rectangle',
  'carre',
  'parallelogramme',
  'losange',
  'trapeze',
  'regulier',
] as const;
export type FormePolygone = (typeof FORMES_POLYGONE)[number];

export const FORMES_SOLIDE = [
  'cube',
  'prisme_droit',
  'cylindre',
  'pyramide',
  'cone',
  'sphere',
] as const;
export type FormeSolide = (typeof FORMES_SOLIDE)[number];

export const BASES_SOLIDE_UI = ['triangle', 'carre', 'rectangle', 'hexagone'] as const;
export type BaseSolideUI = (typeof BASES_SOLIDE_UI)[number];

export const TYPES_DIAGRAMME = ['bandes', 'ligne_brisee', 'nuage_de_points'] as const;
export type TypeDiagramme = (typeof TYPES_DIAGRAMME)[number];

/**
 * LES SOMMETS SONT ENGENDRÉS, JAMAIS SAISIS.
 *
 * Le contrat impose leur NOMBRE selon la forme (trois pour un triangle, quatre
 * pour un quadrilatère, `nbCotes` pour un polygone régulier) et exige des
 * lettres majuscules distinctes. Les faire taper au professeur n'ajoute rien et
 * ouvre une source d'erreur pure : une lettre en double fait refuser la figure,
 * et il devrait deviner pourquoi.
 */
export function sommets(n: number): string[] {
  return Array.from({ length: n }, (_, i) => String.fromCharCode(65 + i));
}

export interface EtatFigure {
  readonly famille: FamilleFigure;
  /* — fonction — */
  readonly fonction: FamilleFonction;
  readonly coef: Coefficients;
  readonly domaine: { xMin: number; xMax: number; yMin: number; yMax: number };
  readonly pas: { x: number; y: number };
  /* — commun aux figures mesurées — */
  readonly unite: Unite;
  /* — triangle — */
  readonly construction: ConstructionTriangle;
  readonly cote1: number;
  readonly cote2: number;
  readonly cote3: number;
  readonly angle1: number;
  readonly angle2: number;
  /* — cercle — */
  readonly rayon: number;
  /* — polygone — */
  readonly formePolygone: FormePolygone;
  readonly largeur: number;
  readonly hauteur: number;
  readonly base: number;
  readonly petiteBase: number;
  readonly grandeBase: number;
  readonly decalage: number;
  readonly angleDeg: number;
  readonly nbCotes: number;
  /* — solide — */
  readonly formeSolide: FormeSolide;
  readonly baseSolide: BaseSolideUI;
  readonly arete: number;
  readonly coteBase: number;
  readonly profondeurBase: number;
  /* — diagramme — */
  readonly typeDiagramme: TypeDiagramme;
  readonly titreAxeX: string;
  readonly titreAxeY: string;
  readonly series: string;
}

export const ETAT_FIGURE_PAR_DEFAUT: EtatFigure = {
  famille: 'fonction',
  fonction: 'lineaire',
  coef: COEF_PAR_DEFAUT,
  domaine: DOMAINE_PAR_DEFAUT,
  pas: PAS_PAR_DEFAUT,
  unite: 'cm',
  construction: 'trois_cotes',
  cote1: 5,
  cote2: 6,
  cote3: 7,
  angle1: 60,
  angle2: 50,
  rayon: 4,
  formePolygone: 'rectangle',
  largeur: 8,
  hauteur: 5,
  base: 6,
  petiteBase: 4,
  grandeBase: 8,
  decalage: 1,
  angleDeg: 60,
  nbCotes: 6,
  formeSolide: 'cube',
  baseSolide: 'carre',
  arete: 4,
  coteBase: 4,
  profondeurBase: 6,
  typeDiagramme: 'bandes',
  titreAxeX: 'Catégorie',
  titreAxeY: 'Valeur',
  series: 'Lundi: 3\nMardi: 5\nMercredi: 4',
};

/**
 * LA SPÉCIFICATION COURANTE, une par famille.
 *
 * Chaque branche ne pose que les champs OBLIGATOIRES du contrat, plus les rares
 * options qui changent vraiment l'énoncé. Le reste a des défauts au contrat, et
 * les exposer tous ferait de ce formulaire une nomenclature plutôt qu'un outil.
 *
 * Aucune validation n'est réimplémentée ici : `resoudreFigure` est le juge, et
 * l'aperçu est son verdict. Une figure impossible ne se dessine pas, et
 * l'enregistrement est alors refusé.
 */
function parseSeries(texte: string): { categorie: string; valeur: number }[] {
  return texte
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const i = l.lastIndexOf(':');
      const categorie = i === -1 ? l : l.slice(0, i).trim();
      const valeur = Number((i === -1 ? '' : l.slice(i + 1)).trim());
      return { categorie, valeur: Number.isFinite(valeur) ? valeur : 0 };
    });
}

function dimensionsPolygone(e: EtatFigure) {
  const u = e.unite;
  switch (e.formePolygone) {
    case 'rectangle':
      return { forme: 'rectangle' as const, largeur: e.largeur, hauteur: e.hauteur, unite: u };
    case 'carre':
      return { forme: 'carre' as const, cote: e.largeur, unite: u };
    case 'parallelogramme':
      return {
        forme: 'parallelogramme' as const,
        base: e.base,
        cote: e.largeur,
        angleDeg: e.angleDeg,
        unite: u,
      };
    case 'losange':
      return { forme: 'losange' as const, cote: e.largeur, angleDeg: e.angleDeg, unite: u };
    case 'trapeze':
      return {
        forme: 'trapeze' as const,
        petiteBase: e.petiteBase,
        grandeBase: e.grandeBase,
        hauteur: e.hauteur,
        decalage: e.decalage,
        unite: u,
      };
    case 'regulier':
      return { forme: 'regulier' as const, nbCotes: e.nbCotes, cote: e.largeur, unite: u };
  }
}

function dimensionsSolide(e: EtatFigure) {
  const u = e.unite;
  switch (e.formeSolide) {
    case 'cube':
      return { forme: 'cube' as const, arete: e.arete, unite: u };
    case 'prisme_droit':
      return {
        forme: 'prisme_droit' as const,
        base: e.baseSolide,
        coteBase: e.coteBase,
        ...(e.baseSolide === 'rectangle' ? { profondeurBase: e.profondeurBase } : {}),
        hauteur: e.hauteur,
        unite: u,
      };
    case 'cylindre':
      return { forme: 'cylindre' as const, rayon: e.rayon, hauteur: e.hauteur, unite: u };
    case 'pyramide':
      return {
        forme: 'pyramide' as const,
        base: e.baseSolide,
        coteBase: e.coteBase,
        ...(e.baseSolide === 'rectangle' ? { profondeurBase: e.profondeurBase } : {}),
        hauteur: e.hauteur,
        unite: u,
      };
    case 'cone':
      return { forme: 'cone' as const, rayon: e.rayon, hauteur: e.hauteur, unite: u };
    case 'sphere':
      return { forme: 'sphere' as const, rayon: e.rayon, unite: u };
  }
}

/** Les données d'un triangle : le contrat en veut trois, deux si rectangle. */
function donneesTriangle(e: EtatFigure) {
  const [A, B, C] = sommets(3) as [string, string, string];
  const cote = (de: string, a: string, valeur: number) => ({
    type: 'cote' as const,
    de,
    a,
    valeur,
    unite: e.unite,
  });
  const angle = (sommet: string, degres: number) => ({ type: 'angle' as const, sommet, degres });
  switch (e.construction) {
    case 'trois_cotes':
      return [cote(A, B, e.cote1), cote(B, C, e.cote2), cote(C, A, e.cote3)];
    case 'deux_cotes_angle':
      return [cote(A, B, e.cote1), cote(A, C, e.cote2), angle(A, e.angle1)];
    case 'cote_deux_angles':
      return [cote(A, B, e.cote1), angle(A, e.angle1), angle(B, e.angle2)];
    case 'rectangle':
      return [cote(A, B, e.cote1), cote(B, C, e.cote2)];
  }
}

export function specDeLEtat(actif: boolean, e: EtatFigure): FigureSpecDto | null {
  if (!actif) return null;
  const titre = LIBELLE_FAMILLE_FIGURE[e.famille];
  switch (e.famille) {
    case 'fonction':
      return specDeLaCourbe(e.fonction, e.coef, e.domaine, e.pas);
    case 'triangle': {
      const [A] = sommets(3);
      return {
        famille: 'triangle',
        titre,
        sommets: sommets(3) as [string, string, string],
        construction: e.construction,
        donnees: donneesTriangle(e),
        ...(e.construction === 'rectangle' ? { sommetAngleDroit: A } : {}),
      };
    }
    case 'cercle':
      return { famille: 'cercle', titre, nomCentre: 'O', rayon: e.rayon, unite: e.unite };
    case 'polygone':
      return {
        famille: 'polygone',
        titre,
        sommets: sommets(e.formePolygone === 'regulier' ? e.nbCotes : 4),
        dimensions: dimensionsPolygone(e),
      };
    case 'solide':
      return { famille: 'solide', titre, dimensions: dimensionsSolide(e) };
    case 'diagramme': {
      const lignes = parseSeries(e.series);
      return {
        famille: 'diagramme',
        titre,
        type: e.typeDiagramme,
        titreAxeX: e.titreAxeX,
        titreAxeY: e.titreAxeY,
        ...(e.typeDiagramme === 'nuage_de_points'
          ? { points: lignes.map((l, i) => ({ x: i + 1, y: l.valeur })) }
          : { series: lignes }),
      };
    }
  }
}

/** Le moteur sait-il tracer la figure courante ? L'aperçu EST le contrôle. */
export function figureIntracable(actif: boolean, e: EtatFigure): boolean {
  const spec = specDeLEtat(actif, e);
  if (spec === null) return false;
  try {
    return resoudreFigure(spec) === null;
  } catch {
    return true;
  }
}

/** Un champ numérique — même habillage partout, pour que la grille reste lisible. */
function Nombre({
  cle,
  libelle,
  valeur,
  onChange,
  pas,
}: {
  cle: string;
  libelle: string;
  valeur: number;
  onChange: (v: number) => void;
  pas?: string;
}) {
  return (
    <span className="field">
      <label htmlFor={`fig-${cle}`}>{libelle}</label>
      <input
        id={`fig-${cle}`}
        className="control"
        type="number"
        step={pas ?? 'any'}
        value={valeur}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </span>
  );
}

function Choix<T extends string>({
  cle,
  libelle,
  valeur,
  options,
  libelles,
  onChange,
}: {
  cle: string;
  libelle: string;
  valeur: T;
  options: readonly T[];
  libelles?: Readonly<Record<string, string>>;
  onChange: (v: T) => void;
}) {
  return (
    <span className="field">
      <label htmlFor={`fig-${cle}`}>{libelle}</label>
      <select
        id={`fig-${cle}`}
        className="control"
        value={valeur}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {libelles?.[o] ?? o}
          </option>
        ))}
      </select>
    </span>
  );
}

export function ChampsFigureFonction({
  actif,
  onActif,
  etat,
  onEtat,
  libelleActivation,
}: {
  actif: boolean;
  onActif: (v: boolean) => void;
  etat: EtatFigure;
  onEtat: (e: EtatFigure) => void;
  libelleActivation: string;
}) {
  const spec = specDeLEtat(actif, etat);
  const intracable = figureIntracable(actif, etat);
  const maj = (patch: Partial<EtatFigure>) => onEtat({ ...etat, ...patch });
  const unite = (
    <Choix
      cle="unite"
      libelle="Unité"
      valeur={etat.unite}
      options={UNITES}
      onChange={(v) => maj({ unite: v })}
    />
  );

  return (
    <fieldset className="fieldset">
      <legend>Une figure ?</legend>
      <p className="card-sub">
        Six familles, et pour chacune ses seules mesures obligatoires. Rien ne se dessine à main
        levée : c’est ce qui garantit que la figure est la même pour vous et pour l’élève.
      </p>
      <label className="field field--inline">
        <input type="checkbox" checked={actif} onChange={(e) => onActif(e.target.checked)} />
        <span>{libelleActivation}</span>
      </label>

      {actif ? (
        <>
          <div className="form-grid">
            <Choix
              cle="famille"
              libelle="Famille"
              valeur={etat.famille}
              options={FAMILLES_FIGURE}
              libelles={LIBELLE_FAMILLE_FIGURE}
              onChange={(v) => maj({ famille: v })}
            />
          </div>

          {etat.famille === 'fonction' ? (
            <>
              <div className="form-grid">
                <Choix
                  cle="fonction"
                  libelle="Fonction"
                  valeur={etat.fonction}
                  options={Object.keys(LIBELLE_FAMILLE) as FamilleFonction[]}
                  libelles={LIBELLE_FAMILLE}
                  onChange={(v) => maj({ fonction: v })}
                />
                {COEFS_PAR_FAMILLE[etat.fonction].map((k) => (
                  <Nombre
                    key={k}
                    cle={`coef-${k}`}
                    libelle={k}
                    valeur={etat.coef[k]}
                    onChange={(v) => maj({ coef: { ...etat.coef, [k]: v } })}
                  />
                ))}
              </div>
              <div className="form-grid">
                {(['xMin', 'xMax', 'yMin', 'yMax'] as const).map((k) => (
                  <Nombre
                    key={k}
                    cle={`dom-${k}`}
                    libelle={k}
                    valeur={etat.domaine[k]}
                    onChange={(v) => maj({ domaine: { ...etat.domaine, [k]: v } })}
                  />
                ))}
                <Nombre
                  cle="pas-x"
                  libelle="Pas en x"
                  valeur={etat.pas.x}
                  onChange={(v) => maj({ pas: { ...etat.pas, x: v || 1 } })}
                />
                <Nombre
                  cle="pas-y"
                  libelle="Pas en y"
                  valeur={etat.pas.y}
                  onChange={(v) => maj({ pas: { ...etat.pas, y: v || 1 } })}
                />
              </div>
            </>
          ) : null}

          {etat.famille === 'triangle' ? (
            <div className="form-grid">
              <Choix
                cle="construction"
                libelle="Ce qui est donné"
                valeur={etat.construction}
                options={CONSTRUCTIONS_TRIANGLE}
                libelles={LIBELLE_CONSTRUCTION}
                onChange={(v) => maj({ construction: v })}
              />
              <Nombre
                cle="c1"
                libelle="AB"
                valeur={etat.cote1}
                onChange={(v) => maj({ cote1: v })}
              />
              {etat.construction !== 'cote_deux_angles' ? (
                <Nombre
                  cle="c2"
                  libelle={etat.construction === 'deux_cotes_angle' ? 'AC' : 'BC'}
                  valeur={etat.cote2}
                  onChange={(v) => maj({ cote2: v })}
                />
              ) : null}
              {etat.construction === 'trois_cotes' ? (
                <Nombre
                  cle="c3"
                  libelle="CA"
                  valeur={etat.cote3}
                  onChange={(v) => maj({ cote3: v })}
                />
              ) : null}
              {etat.construction === 'deux_cotes_angle' ||
              etat.construction === 'cote_deux_angles' ? (
                <Nombre
                  cle="a1"
                  libelle="Angle en A (°)"
                  valeur={etat.angle1}
                  onChange={(v) => maj({ angle1: v })}
                />
              ) : null}
              {etat.construction === 'cote_deux_angles' ? (
                <Nombre
                  cle="a2"
                  libelle="Angle en B (°)"
                  valeur={etat.angle2}
                  onChange={(v) => maj({ angle2: v })}
                />
              ) : null}
              {unite}
            </div>
          ) : null}

          {etat.famille === 'cercle' ? (
            <div className="form-grid">
              <Nombre
                cle="rayon"
                libelle="Rayon"
                valeur={etat.rayon}
                onChange={(v) => maj({ rayon: v })}
              />
              {unite}
            </div>
          ) : null}

          {etat.famille === 'polygone' ? (
            <div className="form-grid">
              <Choix
                cle="forme-poly"
                libelle="Forme"
                valeur={etat.formePolygone}
                options={FORMES_POLYGONE}
                onChange={(v) => maj({ formePolygone: v })}
              />
              {etat.formePolygone === 'regulier' ? (
                <Nombre
                  cle="nbcotes"
                  libelle="Nombre de côtés"
                  valeur={etat.nbCotes}
                  pas="1"
                  onChange={(v) => maj({ nbCotes: Math.max(3, Math.round(v)) })}
                />
              ) : null}
              <Nombre
                cle="largeur"
                libelle={
                  etat.formePolygone === 'rectangle'
                    ? 'Largeur'
                    : etat.formePolygone === 'trapeze'
                      ? 'Côté (non utilisé)'
                      : 'Côté'
                }
                valeur={etat.largeur}
                onChange={(v) => maj({ largeur: v })}
              />
              {etat.formePolygone === 'rectangle' || etat.formePolygone === 'trapeze' ? (
                <Nombre
                  cle="hauteur"
                  libelle="Hauteur"
                  valeur={etat.hauteur}
                  onChange={(v) => maj({ hauteur: v })}
                />
              ) : null}
              {etat.formePolygone === 'parallelogramme' ? (
                <Nombre
                  cle="base"
                  libelle="Base"
                  valeur={etat.base}
                  onChange={(v) => maj({ base: v })}
                />
              ) : null}
              {etat.formePolygone === 'trapeze' ? (
                <>
                  <Nombre
                    cle="pb"
                    libelle="Petite base"
                    valeur={etat.petiteBase}
                    onChange={(v) => maj({ petiteBase: v })}
                  />
                  <Nombre
                    cle="gb"
                    libelle="Grande base"
                    valeur={etat.grandeBase}
                    onChange={(v) => maj({ grandeBase: v })}
                  />
                  <Nombre
                    cle="dec"
                    libelle="Décalage"
                    valeur={etat.decalage}
                    onChange={(v) => maj({ decalage: v })}
                  />
                </>
              ) : null}
              {etat.formePolygone === 'parallelogramme' || etat.formePolygone === 'losange' ? (
                <Nombre
                  cle="angle"
                  libelle="Angle (°)"
                  valeur={etat.angleDeg}
                  onChange={(v) => maj({ angleDeg: v })}
                />
              ) : null}
              {unite}
            </div>
          ) : null}

          {etat.famille === 'solide' ? (
            <div className="form-grid">
              <Choix
                cle="forme-solide"
                libelle="Solide"
                valeur={etat.formeSolide}
                options={FORMES_SOLIDE}
                onChange={(v) => maj({ formeSolide: v })}
              />
              {etat.formeSolide === 'cube' ? (
                <Nombre
                  cle="arete"
                  libelle="Arête"
                  valeur={etat.arete}
                  onChange={(v) => maj({ arete: v })}
                />
              ) : null}
              {etat.formeSolide === 'prisme_droit' || etat.formeSolide === 'pyramide' ? (
                <>
                  <Choix
                    cle="base-solide"
                    libelle="Base"
                    valeur={etat.baseSolide}
                    options={BASES_SOLIDE_UI}
                    onChange={(v) => maj({ baseSolide: v })}
                  />
                  <Nombre
                    cle="cote-base"
                    libelle="Côté de la base"
                    valeur={etat.coteBase}
                    onChange={(v) => maj({ coteBase: v })}
                  />
                  {etat.baseSolide === 'rectangle' ? (
                    <Nombre
                      cle="prof-base"
                      libelle="Profondeur de la base"
                      valeur={etat.profondeurBase}
                      onChange={(v) => maj({ profondeurBase: v })}
                    />
                  ) : null}
                </>
              ) : null}
              {etat.formeSolide === 'cylindre' ||
              etat.formeSolide === 'cone' ||
              etat.formeSolide === 'sphere' ? (
                <Nombre
                  cle="rayon-solide"
                  libelle="Rayon"
                  valeur={etat.rayon}
                  onChange={(v) => maj({ rayon: v })}
                />
              ) : null}
              {etat.formeSolide !== 'cube' && etat.formeSolide !== 'sphere' ? (
                <Nombre
                  cle="hauteur-solide"
                  libelle="Hauteur"
                  valeur={etat.hauteur}
                  onChange={(v) => maj({ hauteur: v })}
                />
              ) : null}
              {unite}
            </div>
          ) : null}

          {etat.famille === 'diagramme' ? (
            <>
              <div className="form-grid">
                <Choix
                  cle="type-diag"
                  libelle="Type"
                  valeur={etat.typeDiagramme}
                  options={TYPES_DIAGRAMME}
                  onChange={(v) => maj({ typeDiagramme: v })}
                />
                <span className="field">
                  <label htmlFor="fig-axe-x">Titre de l’axe des x</label>
                  <input
                    id="fig-axe-x"
                    className="control"
                    value={etat.titreAxeX}
                    onChange={(e) => maj({ titreAxeX: e.target.value })}
                  />
                </span>
                <span className="field">
                  <label htmlFor="fig-axe-y">Titre de l’axe des y</label>
                  <input
                    id="fig-axe-y"
                    className="control"
                    value={etat.titreAxeY}
                    onChange={(e) => maj({ titreAxeY: e.target.value })}
                  />
                </span>
              </div>
              <span className="field field--bloc">
                <label htmlFor="fig-series">
                  Les données — une par ligne, « étiquette : valeur »
                </label>
                <textarea
                  id="fig-series"
                  className="control"
                  rows={4}
                  value={etat.series}
                  onChange={(e) => maj({ series: e.target.value })}
                />
              </span>
            </>
          ) : null}

          {spec !== null && !intracable ? (
            <FigureSvg spec={spec} etiquetteSurface="Ce que l’élève verra" />
          ) : (
            <p className="callout callout--rare" role="alert">
              Cette figure ne se trace pas avec ces valeurs. Corrigez les mesures — elle ne serait
              jamais servie à l’élève.
            </p>
          )}
        </>
      ) : null}
    </fieldset>
  );
}
